import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  rafflesRef,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  subscribeToQuery,
  executeQuery,
  getDocs,
} from '../firebase/firestore';

/**
 * useRaffles
 *
 * Hook for fetching and filtering active raffles.
 * Security: Only queries public raffle data.
 * Note: pickupAddress is stored but should be filtered by Firestore rules
 * to only be visible to the winner. This hook doesn't expose it.
 */

const PAGE_SIZE = 12;

export function useRaffles(options = {}) {
  const {
    category,
    pickupArea,
    status = 'active',
    sortBy = 'endsAt',
    sortOrder = 'asc',
    pageSize = PAGE_SIZE,
    realtime = false,
  } = options;

  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);

  // Build query based on options
  const buildQuery = useCallback(
    (cursor = null) => {
      const constraints = [];

      // Status filter (security: only allow querying public statuses)
      if (status) {
        const allowedStatuses = ['active', 'completed'];
        if (allowedStatuses.includes(status)) {
          constraints.push(where('status', '==', status));
        }
      }

      // Category filter
      if (category) {
        constraints.push(where('category', '==', category));
      }

      // Location filter
      if (pickupArea) {
        constraints.push(where('pickupArea', '==', pickupArea));
      }

      // Sorting
      if (sortBy) {
        constraints.push(orderBy(sortBy, sortOrder));
      }

      // Pagination
      constraints.push(limit(pageSize));

      if (cursor) {
        constraints.push(startAfter(cursor));
      }

      return query(rafflesRef, ...constraints);
    },
    [category, pickupArea, status, sortBy, sortOrder, pageSize]
  );

  // Initial fetch
  useEffect(() => {
    setLoading(true);
    setError(null);
    setLastDoc(null);
    setHasMore(true);

    const q = buildQuery();

    if (realtime) {
      // Real-time subscription
      const unsubscribe = subscribeToQuery(q, (results) => {
        // Filter out sensitive fields on client side (defense in depth)
        const sanitized = results.map(sanitizeRaffle);
        setRaffles(sanitized);
        setLoading(false);
      });

      return unsubscribe;
    } else {
      // One-time fetch
      executeQuery(q)
        .then((results) => {
          const sanitized = results.map(sanitizeRaffle);
          setRaffles(sanitized);
          setHasMore(results.length === pageSize);
          if (results.length > 0) {
            setLastDoc(results[results.length - 1]);
          }
        })
        .catch((err) => {
          console.error('Error fetching raffles:', err);
          setError(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [buildQuery, pageSize, realtime]);

  // Load more (pagination)
  const loadMore = useCallback(async () => {
    if (!hasMore || loading || !lastDoc) return;

    setLoading(true);

    try {
      const q = buildQuery(lastDoc._doc || lastDoc);
      const results = await executeQuery(q);
      const sanitized = results.map(sanitizeRaffle);

      setRaffles((prev) => [...prev, ...sanitized]);
      setHasMore(results.length === pageSize);

      if (results.length > 0) {
        setLastDoc(results[results.length - 1]);
      }
    } catch (err) {
      console.error('Error loading more raffles:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [buildQuery, hasMore, loading, lastDoc, pageSize]);

  // Refresh
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLastDoc(null);
    setHasMore(true);

    try {
      const q = buildQuery();
      const results = await executeQuery(q);
      const sanitized = results.map(sanitizeRaffle);

      setRaffles(sanitized);
      setHasMore(results.length === pageSize);

      if (results.length > 0) {
        setLastDoc(results[results.length - 1]);
      }
    } catch (err) {
      console.error('Error refreshing raffles:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [buildQuery, pageSize]);

  return {
    raffles,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
  };
}

/**
 * useFeaturedRaffles
 *
 * Hook for fetching featured/ending soon raffles for homepage.
 */
export function useFeaturedRaffles(maxItems = 6) {
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        // Get raffles ending soonest
        const q = query(
          rafflesRef,
          where('status', '==', 'active'),
          orderBy('endsAt', 'asc'),
          limit(maxItems)
        );

        const results = await executeQuery(q);
        const sanitized = results.map(sanitizeRaffle);
        setRaffles(sanitized);
      } catch (err) {
        console.error('Error fetching featured raffles:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeatured();
  }, [maxItems]);

  return { raffles, loading, error };
}

/**
 * useRecentRaffles
 *
 * Hook for fetching recently completed raffles.
 */
export function useRecentRaffles(maxItems = 4) {
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const q = query(
          rafflesRef,
          where('status', '==', 'completed'),
          orderBy('completedAt', 'desc'),
          limit(maxItems)
        );

        const results = await executeQuery(q);
        const sanitized = results.map(sanitizeRaffle);
        setRaffles(sanitized);
      } catch (err) {
        console.error('Error fetching recent raffles:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecent();
  }, [maxItems]);

  return { raffles, loading, error };
}

/**
 * Sanitize raffle data to remove sensitive fields.
 * Defense in depth - Firestore rules should also prevent this,
 * but we filter client-side as extra protection.
 */
function sanitizeRaffle(raffle) {
  if (!raffle) return null;

  // Destructure to exclude sensitive fields
  const {
    pickupAddress, // NEVER expose to client
    ...safeData
  } = raffle;

  return safeData;
}

/**
 * CATEGORIES constant for filter UI
 */
export const CATEGORIES = [
  { value: 'electronics', label: 'Electronics' },
  { value: 'furniture', label: 'Furniture' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'fashion', label: 'Fashion' },
  { value: 'sports', label: 'Sports' },
  { value: 'collectibles', label: 'Collectibles' },
  { value: 'home', label: 'Home & Garden' },
  { value: 'toys', label: 'Toys' },
  { value: 'music', label: 'Music' },
  { value: 'books', label: 'Books' },
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'other', label: 'Other' },
];
